import { useState, useEffect, Component, type ReactNode } from 'react';
import { Loader2, TrendingUp, ToggleLeft, ToggleRight } from 'lucide-react';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import { OrgKeysPanel } from './OrgKeysPanel';
import { PolymarketOrgKeysPanel } from './PolymarketOrgKeysPanel';
import { OrgBotDefaultsPanel } from './OrgBotDefaultsPanel';
import { TriggerRecoveryPanel } from './TriggerRecoveryPanel';
import { AutomationWindDownPanel } from './AutomationWindDownPanel';
import { useOrgScope } from '../../stores/orgScope';
import { formatMoney } from '../../lib/infra/formatters';
import { useMemberGovernance } from './useMemberGovernance';
import { MembersPanel } from './MembersPanel';
import { ExportsPanel } from './ExportsPanel';
import { useExportWorkflows } from './useExportWorkflows';

// Re-exported so `./index` keeps its public contract: memberCardLayout.test.tsx
// and managerExports.test.tsx import MemberCard from here, and managerExports
// also imports OrgTradeExportPanel from here (#343 moved the implementation to
// OrgCsvExportPanels.tsx; the import path callers use is unchanged).
export { MemberCard } from './MemberCard';
export { OrgTradeExportPanel } from './OrgCsvExportPanels';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-8">
          <p className="text-sm text-red-500">Something went wrong loading this view.</p>
          <button onClick={() => this.setState({ error: null })}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-500">
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface ManagerDashboardProps {
  currentView: string;
  adminMode?: boolean;
  view?: 'settings' | 'exports';
}

// `currentView` stays on the props interface — App.tsx and twelve specs pass it,
// and widening that blast radius is not this ticket's job — but it is not read
// here and is deliberately not destructured. `view` is the prop that selects the
// pane; `currentView` is a caller-side label.
export function ManagerDashboard({ adminMode, view = 'settings' }: ManagerDashboardProps) {
  return (
    <ErrorBoundary>
      <div className="h-full overflow-auto p-6">
        <TradersPanel adminMode={adminMode} view={view} />
      </div>
    </ErrorBoundary>
  );
}

// ── Traders Panel ───────────────────────────────────────────────

function TradersPanel({ adminMode, view = 'settings' }: { adminMode?: boolean; view?: 'settings' | 'exports' }) {
  // Org scope is owned by the shared TabShell layout (one selector across the
  // Organization tabs) via useOrgScope. Non-admin managers always scope to their
  // own org, so selectedOrgId stays '' for them.
  const storeSelectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const storeOrgs = useOrgScope((s) => s.orgs);
  const orgsLoaded = useOrgScope((s) => s.orgsLoaded);
  const ensureOrgs = useOrgScope((s) => s.ensureOrgs);
  const selectedOrgId = adminMode ? storeSelectedOrgId : '';
  const orgs = adminMode ? storeOrgs : [];
  const adminOrgsLoaded = adminMode ? orgsLoaded : true;

  // Defensive: ensure the admin org list is loaded even if this panel mounts before
  // TabShell's selector. Idempotent — the store guards on its own orgsLoaded flag.
  useEffect(() => {
    if (adminMode) ensureOrgs();
  }, [adminMode, ensureOrgs]);

  const orgQueryParam = adminMode ? `?orgId=${selectedOrgId}` : '';

  // #295 — the org a new Manager would be bound to, RESOLVED against the loaded
  // org list rather than trusted as a bare id. `selectedOrgId` is rehydrated from
  // localStorage (stores/orgScope.ts), so it can name an org that was since
  // deleted, or one this admin's list never contained — the ticket's "never
  // create a Manager in a stale or previously selected organization". Resolving
  // it is also what lets the form NAME the target org instead of asking the
  // admin to trust a toolbar they may not have looked at.
  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null;

  // Member/trader governance: the member list, usage rollup, org bot gate, role
  // predicates and every governance write now live in one hook (#342). This
  // panel keeps the export workflows and composes the two.
  const gov = useMemberGovernance({ adminMode, selectedOrgId, orgQueryParam, selectedOrg, adminOrgsLoaded });

  // #461 — the effective organization a Google trader selection belongs to,
  // and whether members may be trusted for it yet. Keyed on the RESOLVED org,
  // not selectedOrgId: a stale/deleted stored id is non-empty but names no
  // loaded org, and must read as unresolved. The sentinels cannot collide with
  // org UUIDs. `gov.loading` is derived from which org the list was read for,
  // so readiness drops on the same render the scope changes — governance keeps
  // the outgoing members array during a switch (see useMemberGovernance), and
  // this is what keeps those members out of the Google panels.
  //
  // #486 — readiness now has a SECOND consumer: the panels' action buttons gate
  // on it directly (`googleScopeReady`). The name still says "trader" because
  // the scope being resolved is the one a trader selection belongs to, but it
  // is no longer only the selection that depends on it.
  const googleTraderScopeKey = adminMode
    ? (selectedOrg?.id ?? 'admin:unselected')
    : 'manager:own-org';
  const googleTraderScopeReady = !gov.loading && (!adminMode || selectedOrg !== null);

  // Export workflows: the three CSV downloads, both Google Sheets tools, and
  // the per-member export slots. Governance supplies the member list and
  // nothing else (#343) — every export error is owned by its own panel.
  const ex = useExportWorkflows({
    orgQueryParam,
    members: gov.members,
    googleTraderScopeKey,
    googleTraderScopeReady,
  });

  const orgKeysVisible = !adminMode || (adminMode && !!selectedOrgId);

  return (
    <section>
      {/* The org selector lives in TabShell now; this panel only flags the empty case. */}
      {adminMode && adminOrgsLoaded && orgs.length === 0 && (
        <div className="bg-muted/50 border-border mb-4 rounded-lg border p-6 text-center">
          <p className="text-muted-foreground text-sm">No organizations exist yet.</p>
          <p className="text-muted-foreground mt-1 text-xs">Create one in the Admin panel to manage traders.</p>
        </div>
      )}

      {orgKeysVisible && view === 'settings' && (
        <>
          <div className="mb-6 space-y-6 rounded-lg border border-border bg-card p-4">
            <OrgKeysPanel orgQueryParam={orgQueryParam} embedded />
            <PolymarketOrgKeysPanel orgQueryParam={orgQueryParam} embedded />
          </div>
          <OrgMmRebatePanel orgQueryParam={orgQueryParam} className="mb-6" />
          <OrgBotDefaultsPanel orgQueryParam={orgQueryParam} onEnabledChange={gov.setOrgBotsEnabled} className="mb-6" />
          {/* #287: frozen moved-owner triggers. Deliberately its own org-level
              panel rather than an action on the moved trader's member row — the
              trader has no authority here at all, and the work is scoped to the
              organization the orders were placed under. */}
          <TriggerRecoveryPanel
            adminMode={adminMode}
            orgQueryParam={orgQueryParam}
            selectedOrgId={selectedOrgId}
            className="mb-6"
          />
          {/* #670: org-level emergency control for #300. Below trigger
              recovery — same audience, same org scoping, and the recovery
              panel is the reference grammar for this one. */}
          <AutomationWindDownPanel
            adminMode={adminMode}
            selectedOrg={selectedOrg}
            orgsLoaded={adminOrgsLoaded}
            className="mb-6"
          />
        </>
      )}
      {orgKeysVisible && view === 'exports' && (
        <ExportsPanel adminMode={adminMode} ex={ex} />
      )}

      <MembersPanel
        view={view}
        adminMode={adminMode}
        selectedOrgId={selectedOrgId}
        orgQueryParam={orgQueryParam}
        selectedOrg={selectedOrg}
        governance={gov}
        exportSlotsFor={ex.exportSlotsFor}
      />
    </section>
  );
}

// ── Org MM Rebate Panel ─────────────────────────────────────────
// Org-level opt-in for Kalshi's
// Market Maker Program (the enroll toggle) plus the org-wide expected-rebate
// status — the sum of the per-fill est_rebate_cents the relay stores at fill
// time, over the program's rolling 30-day window. Org-scoped via
// orgQueryParam (manager → own org; admin → selected org).

interface OrgRebateSummary {
  estRebateCents: number;
  fillCount: number;
  windowDays: number;
}

function OrgMmRebatePanel({
  orgQueryParam,
  className = 'mb-6',
}: {
  orgQueryParam: string;
  className?: string;
}) {
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // #349 — each answer is STAMPED with the org it was fetched for, and the
  // rendered value is derived by matching that stamp against the current org.
  // The panel therefore blanks on an org switch by derivation rather than by a
  // reset scheduled inside the effect: there is no window in which the previous
  // org's enrolment or rebate tally renders under the new org's name, and no
  // synchronous setState in an effect to schedule it. Same derive-don't-store
  // shape as the crossed-book tripwire (#167).
  const [enabledFor, setEnabledFor] = useState<{ org: string; value: boolean } | null>(null);
  const [summaryFor, setSummaryFor] = useState<{ org: string; value: OrgRebateSummary } | null>(
    null,
  );
  const enabled = enabledFor?.org === orgQueryParam ? enabledFor.value : null;
  const summary = summaryFor?.org === orgQueryParam ? summaryFor.value : null;

  // Two independent reads, deliberately kept PARALLEL as sibling async IIFEs
  // rather than awaited in sequence — the summary must not wait on the
  // enrolment. The summary fetch was a `useCallback` used at exactly one call
  // site, which existed only to satisfy the dependency array; inlining it also
  // moves its setState behind an await where the diagnostic expects it. An
  // extracted async helper reads to the rule as a direct setState call, an
  // inline one does not.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org${orgQueryParam}`);
        const data = await res.json();
        if (!cancelled) {
          setEnabledFor({ org: orgQueryParam, value: res.ok ? !!data.org?.mmRebateEnabled : false });
        }
      } catch {
        if (!cancelled) setEnabledFor({ org: orgQueryParam, value: false });
      }
    })();
    (async () => {
      try {
        const res = await apiFetch(`/api/manager/org/rebate-summary${orgQueryParam}`);
        if (!res.ok) return;
        const s = await res.json();
        setSummaryFor({
          org: orgQueryParam,
          value: {
            estRebateCents: s.estRebateCents ?? 0,
            fillCount: s.fillCount ?? 0,
            windowDays: s.windowDays ?? 30,
          },
        });
      } catch {
        /* best-effort — status line just stays hidden */
      }
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam]);

  const handleToggle = async () => {
    if (enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mmRebateEnabled: next }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? 'Update failed'); return; }
      setEnabledFor({ org: orgQueryParam, value: !!data.org?.mmRebateEnabled });
      // Pull the change into the caller's own session so their dashboard picks
      // it up without a reload (no-op for an admin acting on another org).
      void refreshMe();
    } catch {
      setError('Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold">Market Maker Rebate</h3>
        </div>
        <button
          onClick={handleToggle}
          disabled={enabled === null || saving}
          title={enabled ? 'Enrolled — click to disable' : 'Not enrolled — click to enable'}
          className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-xs font-medium disabled:opacity-50"
        >
          {saving || enabled === null ? (
            <Loader2 size={16} className="animate-spin" />
          ) : enabled ? (
            <ToggleRight size={18} className="text-emerald-500" />
          ) : (
            <ToggleLeft size={18} className="text-muted-foreground" />
          )}
          <span>{enabled === null ? '' : enabled ? 'Enrolled' : 'Disabled'}</span>
        </button>
      </div>

      <p className="text-muted-foreground mb-3 text-[11px]">
        Enroll this org in Kalshi's Market Maker Program so trade tickets and
        ladders show maker fees net of the rebate (50% pregame, 100% live). The
        rebate settles separately with Kalshi — this controls the in-app
        estimate and the expected-rebate tally below.
      </p>

      {summary && (
        <div className="bg-muted/40 border-border flex items-center justify-between rounded-md border px-3 py-2">
          <div>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">
              Est. rebate · last {summary.windowDays}d
            </div>
            <div className="text-muted-foreground/70 text-[10px]">
              {summary.fillCount.toLocaleString()} maker fill
              {summary.fillCount === 1 ? '' : 's'} · uncapped estimate
            </div>
          </div>
          <span className="font-mono text-base font-semibold text-emerald-500">
            {formatMoney(summary.estRebateCents / 100)}
          </span>
        </div>
      )}

      {error && <div className="mt-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>}
    </section>
  );
}
